import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, X } from "lucide-react";
import { buildOutlook, loadTone, type OutlookCell, type OutlookPerson, type OutlookWeek } from "@/lib/capacityOutlook";
import { cn } from "@/lib/cn";
import type { Project } from "@/types/project";
import type { TeamMember } from "@/types/roster";
import type { Assignment } from "@/hooks/useAssignments";

/**
 * Capacity Outlook — 13 weeks of who's committed where, and when hours
 * come free (Brett, 2026-09-21). Pure arithmetic on the roster,
 * assignments, and go-live dates the page already has; nothing new to
 * enter. Answers the board's "when X finishes, who's available?"
 *
 * Cells read as PERCENT of the person's (or team's) available hours
 * (Brett, same day: "73 vs 101.5 should be a percentage"). Click a
 * name or a cell for the panel that names the projects behind it,
 * each linked to its project page.
 *
 * Only ACTIVE hours build the percentage (role gate — see
 * lib/capacityOutlook.ts): a developer booked on a project still in
 * functional spec is QUEUED, shown as a muted "+35 queued" under the
 * percent so the pipeline behind the load stays visible.
 */

const fmtH = (v: number) => (Number.isInteger(v) ? `${v}` : v.toFixed(1));
const fmtPct = (committed: number, available: number) =>
  available > 0 ? `${Math.round((committed / available) * 100)}%` : committed > 0 ? "∞" : "·";

const TONE_CLASS = {
  idle: "bg-canvas text-ink-faint",
  ok: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
  full: "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300",
  over: "bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300 font-semibold",
} as const;

type Focus = { person: OutlookPerson; week: number | null };

export function CapacityOutlook({
  people,
  assignments,
  projects,
  countedTiers,
}: {
  people: TeamMember[];
  assignments: Assignment[];
  projects: Project[];
  /** Capacity scope (2026-10-06): which board bands count. */
  countedTiers?: Set<string>;
}) {
  const outlook = useMemo(
    () => buildOutlook({ people, assignments, projects, weeks: 13, countedTiers }),
    [people, assignments, projects, countedTiers],
  );
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState<Focus | null>(null);
  const toggleTeam = (t: string) =>
    setCollapsedTeams((s) => {
      const n = new Set(s);
      if (n.has(t)) n.delete(t); else n.add(t);
      return n;
    });

  const anyPeople = outlook.teams.some((t) => t.people.length > 0);

  return (
    <section className="space-y-3" data-testid="capacity-outlook">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-ink-muted">
            Outlook · next 13 weeks
          </h2>
          <p className="text-xs text-ink-muted">
            Each cell is the share of available hours committed that week, from each project's go-live.
            Hours count once the project reaches the stage where that role starts (PM and BA from Approved;
            functional, technical, and developers from Technical spec). Booked hours before that show as queued.
            Click a name or a cell to see the projects behind it.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-ink-muted">
          <Legend tone="ok" label="under 85%" />
          <Legend tone="full" label="85–100%" />
          <Legend tone="over" label="over 100%" />
          <span className="text-ink-subtle">+N queued = booked, role not started</span>
        </div>
      </div>

      {(outlook.pastDueHours > 0 || outlook.undatedHours > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 px-4 py-2 text-xs text-amber-800 dark:text-amber-300" data-testid="outlook-flags">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {outlook.pastDueHours > 0 && (
            <span>
              <b>{fmtH(outlook.pastDueHours)}h/wk</b> is held by projects past their go-live — they keep counting until closed out or re-dated.
            </span>
          )}
          {outlook.undatedHours > 0 && (
            <span>
              <b>{fmtH(outlook.undatedHours)}h/wk</b> is on projects with no go-live date, so it never frees up here.
            </span>
          )}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* ---- Grid ---- */}
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          {!anyPeople ? (
            <div className="p-8 text-center text-sm text-ink-subtle">No one is in the capacity roster yet.</div>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-line text-[10px] uppercase tracking-wider text-ink-muted">
                  <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-semibold">Person</th>
                  <th className="px-2 py-2 text-right font-semibold" title="Hours available per week">Avail</th>
                  {outlook.weeks.map((w, i) => (
                    <th key={w.start} className={cn("px-1 py-2 text-center font-semibold tabular-nums", i === 0 && "text-brand-navy-700 dark:text-brand-navy-300")} title={`Week of ${w.start}`}>
                      {w.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {outlook.teams.map((t) => {
                  const collapsed = collapsedTeams.has(t.team);
                  return (
                    <TeamRows
                      key={t.team}
                      team={t.team}
                      available={t.available}
                      cells={t.cells}
                      people={t.people}
                      collapsed={collapsed}
                      onToggle={() => toggleTeam(t.team)}
                      onFocus={(person, week) => setFocus({ person, week })}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ---- Releases ---- */}
        <div className="rounded-xl border border-line bg-surface" data-testid="outlook-releases">
          <div className="flex items-center gap-2 border-b border-line-soft px-4 py-2.5">
            <CalendarClock className="h-4 w-4 text-ink-muted" />
            <span className="text-sm font-semibold text-ink">Who frees up, and when</span>
          </div>
          {outlook.releases.length === 0 ? (
            <div className="p-6 text-center text-xs text-ink-subtle">No dated assignments yet.</div>
          ) : (
            <ul className="max-h-[520px] divide-y divide-line-soft overflow-y-auto">
              {outlook.releases.map((r) => (
                <li key={r.date ?? "none"} className="px-4 py-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={cn("text-sm font-semibold tabular-nums", r.pastDue ? "text-amber-700 dark:text-amber-300" : "text-ink")}>
                      {r.label}
                      {r.pastDue && <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wide">past due</span>}
                    </span>
                    <span className="text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">+{fmtH(r.totalHours)}h/wk</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-ink-muted">
                    {r.projects.map((p, i) => (
                      <span key={p.id}>
                        {i > 0 && " · "}
                        <Link to={`/portfolio/${p.id}`} className="font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline">{p.id}</Link> {p.name}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.people.map((p) => (
                      <span key={p.name} className="rounded-full bg-raised px-1.5 py-0.5 text-[10px] text-ink-2">
                        {p.name} <span className="font-semibold tabular-nums">{fmtH(p.hours)}h</span>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {focus && <PersonPanel focus={focus} weeks={outlook.weeks} onClose={() => setFocus(null)} onWeek={(w) => setFocus({ ...focus, week: w })} />}
    </section>
  );
}

function Legend({ tone, label }: { tone: keyof typeof TONE_CLASS; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-block h-3 w-3 rounded-sm ring-1 ring-inset ring-black/5", TONE_CLASS[tone].split(" ")[0])} />
      {label}
    </span>
  );
}

function TeamRows({
  team, available, cells, people, collapsed, onToggle, onFocus,
}: {
  team: string; available: number; cells: OutlookCell[]; people: OutlookPerson[]; collapsed: boolean;
  onToggle: () => void; onFocus: (p: OutlookPerson, week: number | null) => void;
}) {
  return (
    <>
      <tr className="border-b border-line-soft bg-canvas/80" data-outlook-team={team}>
        <td className="sticky left-0 z-10 bg-canvas/80 px-3 py-1.5">
          <button type="button" onClick={onToggle} className="flex items-center gap-1.5 text-left font-semibold text-ink-2" aria-expanded={!collapsed}>
            <span className={cn("inline-block text-[10px] transition-transform", collapsed && "-rotate-90")}>▾</span>
            {team}
            <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-ink-3">{people.length}</span>
          </button>
        </td>
        <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-ink-2">{fmtH(available)}</td>
        {cells.map((c, i) => (
          <Cell key={i} cell={c} available={available} strong title={`${fmtH(c.committed)}h active of ${fmtH(available)}h across the team${c.queued ? ` · ${fmtH(c.queued)}h queued` : ""}`} />
        ))}
      </tr>
      {!collapsed &&
        people.map((p) => (
          <tr key={p.name} className="border-b border-line-soft" data-outlook-person={p.name}>
            <td className="sticky left-0 z-10 bg-surface px-3 py-1.5 pl-7">
              <button
                type="button"
                onClick={() => onFocus(p, null)}
                className="text-left font-medium text-ink hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline"
                title="See this person's projects"
              >
                {p.name}
              </button>
              {p.holds.length > 0 && (
                <span className="ml-2 text-[10px] text-ink-subtle">
                  {p.holds.length} project{p.holds.length === 1 ? "" : "s"}
                </span>
              )}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-ink-muted">{fmtH(p.available)}</td>
            {p.cells.map((c, i) => (
              <Cell
                key={i}
                cell={c}
                available={p.available}
                title={`${fmtH(c.committed)}h active of ${fmtH(p.available)}h${c.queued ? ` · ${fmtH(c.queued)}h queued` : ""} — click for the projects`}
                onClick={() => onFocus(p, i)}
              />
            ))}
          </tr>
        ))}
    </>
  );
}

function Cell({ cell, available, strong, title, onClick }: { cell: OutlookCell; available: number; strong?: boolean; title?: string; onClick?: () => void }) {
  const tone = loadTone(cell.committed, available);
  return (
    <td
      className={cn("px-1 py-1.5 text-center tabular-nums", TONE_CLASS[tone], strong && "font-semibold", onClick && "cursor-pointer hover:ring-1 hover:ring-inset hover:ring-brand-navy-300")}
      title={title}
      onClick={onClick}
    >
      <div>{fmtPct(cell.committed, available)}</div>
      {cell.queued > 0 && (
        <div className="text-[9px] font-normal leading-tight text-ink-subtle" title="Booked, but this role hasn't started on the project yet">
          +{fmtH(cell.queued)} queued
        </div>
      )}
    </td>
  );
}

/** Slide-over: one person's projects, each linked, with the chosen
 *  week's active set called out. */
function PersonPanel({ focus, weeks, onClose, onWeek }: { focus: Focus; weeks: OutlookWeek[]; onClose: () => void; onWeek: (w: number | null) => void }) {
  const { person, week } = focus;
  const weekLabel = week != null ? weeks[week].label : null;
  // Week view: holds live that week, active + queued (off never counts).
  // "All": every hold, including off, so nothing booked is hidden.
  const inWeek = week != null ? person.holds.filter((h) => h.weeks[week] && h.state !== "off") : person.holds;
  const committed = inWeek.filter((h) => h.state === "active").reduce((s, h) => s + h.hours, 0);
  const queued = inWeek.filter((h) => h.state === "queued").reduce((s, h) => s + h.hours, 0);
  const activeAll = person.holds.filter((h) => h.state === "active").reduce((s, h) => s + h.hours, 0);
  const queuedAll = person.holds.filter((h) => h.state === "queued").reduce((s, h) => s + h.hours, 0);
  return (
    <>
      <button className="fixed inset-0 z-30 bg-slate-900/20" onClick={onClose} aria-label="Close" />
      <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col bg-surface shadow-2xl" data-testid="outlook-person-panel" role="dialog" aria-label={`${person.name} projects`}>
        <div className="flex items-start justify-between border-b border-line-soft px-5 py-4">
          <div>
            <div className="text-base font-semibold text-ink">{person.name}</div>
            <div className="text-xs text-ink-muted">
              {person.team} · {fmtH(person.available)}h/wk available · {fmtH(activeAll)}h/wk active
              {queuedAll > 0 && <> · {fmtH(queuedAll)}h/wk queued</>} · {person.holds.length} project{person.holds.length === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-2" aria-label="Close panel">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Week selector — which week are we looking at? */}
        <div className="flex flex-wrap gap-1 border-b border-line-soft px-5 py-2">
          <button
            onClick={() => onWeek(null)}
            className={cn("rounded-full px-2 py-0.5 text-[11px]", week == null ? "bg-slate-900 text-white" : "bg-raised text-ink-3 hover:bg-muted")}
          >
            All
          </button>
          {weeks.map((w, i) => (
            <button
              key={w.start}
              onClick={() => onWeek(i)}
              className={cn("rounded-full px-2 py-0.5 text-[11px] tabular-nums", week === i ? "bg-slate-900 text-white" : "bg-raised text-ink-3 hover:bg-muted")}
            >
              {w.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-3 text-xs text-ink-3">
          {weekLabel ? (
            <>Week of <b>{weekLabel}</b>: <b>{fmtH(committed)}h</b> of {fmtH(person.available)}h ({fmtPct(committed, person.available)}) active
              {queued > 0 && <>, <b>{fmtH(queued)}h</b> queued</>} on {inWeek.length} project{inWeek.length === 1 ? "" : "s"}.</>
          ) : (
            <>Every project this person holds hours on, soonest go-live first.</>
          )}
        </div>

        <ul className="flex-1 divide-y divide-line-soft overflow-y-auto">
          {inWeek.length === 0 && (
            <li className="px-5 py-8 text-center text-sm text-ink-subtle">Nothing committed that week.</li>
          )}
          {inWeek.map((h) => (
            <li key={h.id} className="px-5 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <Link to={`/portfolio/${h.id}`} className="min-w-0 truncate text-sm font-medium text-brand-navy-700 dark:text-brand-navy-300 hover:underline" onClick={onClose}>
                  {h.name}
                </Link>
                <span className={cn("shrink-0 text-sm font-semibold tabular-nums", h.state === "active" ? "text-ink" : "text-ink-subtle line-through decoration-ink-faint")}>{fmtH(h.hours)}h/wk</span>
              </div>
              {h.state !== "active" && (
                <div className="mt-0.5">
                  <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-semibold", h.state === "queued" ? "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300" : "bg-raised text-ink-muted")}>
                    {h.state === "queued" ? "queued" : "not counted"} — {h.reason}
                  </span>
                </div>
              )}
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-ink-muted">
                <span className="font-mono">{h.id}</span>
                <span>·</span>
                {h.endDate ? (
                  <span>
                    go-live {h.endDate}
                    {h.pastDue && <span className="ml-1 rounded-full bg-amber-100 dark:bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">past due — still holding</span>}
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-100 dark:bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">no go-live date — never frees up</span>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="border-t border-line-soft px-5 py-2 text-[11px] text-ink-subtle">
          Hours are edited in the People ledger above or on each project's Team card.
        </div>
      </aside>
    </>
  );
}
